import { useEffect, useState } from 'react';
import { useAlert } from 'react-alert';
import { Link } from 'react-router-dom';
import { ApiHelper } from '../../apiHelper/ApiHelper';
import { readJson } from '../../apiHelper/readJson';
import ConfirmModal from '../../components/ConfirmModal';
import SlotPicker from './SlotPicker';
import { time, groupByDay } from './lessonDates';

type Slot = { id: string; start: string; durationMinutes: number };
type Booked = { id: string; start: string; durationMinutes: number; topic: string; meetUrl: string; canCancel: boolean };

// The "book an extra individual lesson" card, shown across every journey status.
// Self-contained: fetches the student's credit balance, the open lecturer slots,
// and the student's own already-booked extra lectures (so they stay visible even
// during voting / waiting, before the regular schedule exists). Refreshes after a
// booking or cancellation. Renders inside a `.journey` container so it inherits the
// journey card styling (individualJourney.scss).
export default function BookingCalendar() {
    const api = new ApiHelper();
    const alert = useAlert();

    const [slots, setSlots] = useState<Slot[]>([]);
    const [booked, setBooked] = useState<Booked[]>([]);
    const [credit, setCredit] = useState(0);
    const [loaded, setLoaded] = useState(false);
    const [cancelId, setCancelId] = useState<string | null>(null);

    async function fetchBooking() {
        try {
            const [bookRes, schedRes] = await Promise.all([
                api.onlineLessonsApi.getBookingList(),
                api.onlineLessonsApi.getScheduleList(),
            ]);
            const data = await readJson(bookRes);
            setCredit(data.creditBalance ?? 0);
            setSlots(data.slots ?? []);
            const sched = await readJson(schedRes);
            // Only the student's own extra (individually booked) lectures — regular
            // group lectures belong to the schedule view, not the booking card.
            setBooked((sched.upcoming ?? []).filter((l: any) => l.isExtra));
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setLoaded(true);
        }
    }

    useEffect(() => { fetchBooking(); }, []);

    async function book(slotId: string) {
        try {
            await api.onlineLessonsApi.bookCreate(slotId);
            alert.success("Termín byl rezervován");
            fetchBooking();
        } catch (x: any) { api.processErrors(x); }
    }

    async function cancel() {
        if (!cancelId) return;
        try {
            await api.onlineLessonsApi.cancelCreate(cancelId);
            alert.success("Rezervace zrušena, kredit byl vrácen");
            fetchBooking();
        } catch (x: any) {
            api.processErrors(x);
        } finally {
            setCancelId(null);
        }
    }

    const bookedDays = groupByDay(booked);

    return (
        <section className="jCard">
            <h2>Individuální lekce navíc</h2>
            <p className="sub">Potřebuješ něco doladit? Rezervuj si individuální lekci s lektorem navíc.</p>

            {booked.length > 0 &&
                <div className="calendar booked">
                    {bookedDays.map((g, gi) => (
                        <div key={gi} className="calDay">
                            <div className="dateBox"><span className="d">{g.day}.</span><span className="m">{g.month}</span></div>
                            <div className="items">
                                {g.items.map(l => (
                                    <div key={l.id} className="item">
                                        <div>
                                            <span className="time">{time(l.start)} · {l.durationMinutes} min</span>
                                            <h3>{l.topic || "Individuální lekce"}</h3>
                                        </div>
                                        <div className="acts">
                                            {l.meetUrl
                                                ? <button className="btn join" onClick={() => window.open(l.meetUrl, "_blank", "noopener")}>Připojit</button>
                                                : <span className="empty">Odkaz bude doplněn</span>}
                                            {l.canCancel && <button className="btn cancel" onClick={() => setCancelId(l.id)}>Zrušit</button>}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    ))}
                </div>
            }

            <p className="credit">Máš <b>{credit}</b> {credit === 1 ? "kredit" : (credit >= 2 && credit <= 4 ? "kredity" : "kreditů")} na lekce navíc.</p>
            {!loaded ?
                <p className="empty">Načítání…</p>
                : credit <= 0 ?
                <p className="empty">Nemáš žádný kredit. <Link to="/kurz/ucet">Dokup v Účtu →</Link></p>
                : slots.length === 0 ?
                <p className="empty">Momentálně nejsou volné žádné termíny.</p>
                :
                <SlotPicker slots={slots} onBook={book} />
            }

            <ConfirmModal
                open={cancelId !== null}
                title="Zrušit rezervaci?"
                message="Rezervace bude zrušena a kredit ti bude vrácen na účet."
                confirmLabel="Zrušit rezervaci"
                cancelLabel="Zpět"
                onConfirm={cancel}
                onClose={() => setCancelId(null)}
            />
        </section>
    );
}
